import { useState } from 'react';
import type { KeyedMutator } from 'swr';
import { Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useCommunity } from '@/hooks/use-community';
import {
  SegmentedTabs,
  SegmentedTabsContent,
  SegmentedTabsList,
  SegmentedTabsTrigger,
} from '@/components/ui/segmented-tabs';
import type { Community } from '@/types';
import { GeneralSettings } from './components/GeneralSettings';
import { RoleAccessSettings } from './components/RoleAccessSettings';

type CommunityMutate = KeyedMutator<{ community: Community }>;

function AggregatorSettingsPanels({
  communityId,
  community,
  mutateCommunity,
}: {
  communityId: string;
  community: Community;
  mutateCommunity: CommunityMutate;
}) {
  const [name, setName] = useState(() => community.name || '');
  const [slug, setSlug] = useState(() => community.slug || '');
  const [logo, setLogo] = useState(() => community.logo || '');
  const [banner, setBanner] = useState(() => community.banner || '');
  const inviteCodeEnabled = community.inviteCodeEnabled !== false;
  const [originalSlug, setOriginalSlug] = useState(() => community.slug || '');
  const [inviteLink, setInviteLink] = useState(community.inviteLink || '');
  const inviteCode = community.inviteCode || '';

  return (
    <div className="container mx-auto py-10 px-4 max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-2">Manage your aggregator profile and roles.</p>
        </div>
      </div>

      <SegmentedTabs defaultValue="general" className="space-y-6">
        <SegmentedTabsList variant="compact" className="w-full">
          <SegmentedTabsTrigger variant="compact" value="general">
            General
          </SegmentedTabsTrigger>
          <SegmentedTabsTrigger variant="compact" value="roles">
            Roles
          </SegmentedTabsTrigger>
        </SegmentedTabsList>

        <SegmentedTabsContent value="general">
          <GeneralSettings
            communityId={communityId}
            mutateCommunity={mutateCommunity}
            name={name}
            setName={setName}
            slug={slug}
            setSlug={setSlug}
            logo={logo}
            setLogo={setLogo}
            banner={banner}
            setBanner={setBanner}
            originalSlug={originalSlug}
            setOriginalSlug={setOriginalSlug}
            showNavigation={false}
            inviteLink={inviteLink}
            setInviteLink={setInviteLink}
            inviteCode={inviteCode}
            setInviteCode={() => undefined}
            inviteCodeEnabled={inviteCodeEnabled}
            themeColors={(community.themeColors as { primary?: string | null } | null) ?? null}
          />
        </SegmentedTabsContent>

        <SegmentedTabsContent value="roles">
          <RoleAccessSettings communityId={communityId} />
        </SegmentedTabsContent>
      </SegmentedTabs>
    </div>
  );
}

export default function AggregatorSettingsPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const {
    community: communityPayload,
    mutate: mutateCommunity,
    isLoading,
  } = useCommunity(communityId);

  if (!communityId) return null;

  const community = communityPayload?.community;

  if (isLoading || !community) {
    return (
      <div className="container mx-auto py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm font-medium">Loading settings…</p>
      </div>
    );
  }

  return (
    <AggregatorSettingsPanels
      key={`${communityId}:${community.updatedAt}`}
      communityId={communityId}
      community={community}
      mutateCommunity={mutateCommunity}
    />
  );
}
